import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faMinus, faPlus, faEdit, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { SalonWorkingHoursResponse } from '@api/generated';

interface WorkingHoursData {
  [key: string]: {
    closed: boolean;
    intervals: Array<{ start: string; end: string; isActive: boolean }>;
  };
}

interface WorkingHoursProps {
  workingHours: SalonWorkingHoursResponse[];
  onSave: (data: WorkingHoursData) => Promise<void>;
}

const days = [
  { id: '0', name: 'Neděle' },
  { id: '1', name: 'Pondělí' },
  { id: '2', name: 'Úterý' },
  { id: '3', name: 'Středa' },
  { id: '4', name: 'Čtvrtek' },
  { id: '5', name: 'Pátek' },
  { id: '6', name: 'Sobota' },
];

const WorkingHours: React.FC<WorkingHoursProps> = ({ workingHours, onSave }) => {
  const [isEditingHours, setIsEditingHours] = useState(false);
  const [salonData, setSalonData] = useState<WorkingHoursData>({});

  // Načtení dat z API
  useEffect(() => {
    if (workingHours) {
      const newData: WorkingHoursData = {};

      // Inicializace všech dnů jako zavřené
      days.forEach(day => {
        newData[day.id] = { closed: true, intervals: [{ start: '09:00', end: '17:00', isActive: true }] };
      });

      // Naplnění dat z API pouze pokud existují
      if (workingHours.length > 0) {
        workingHours.forEach(wh => {
          const dayId = wh.weekday.toString();
          newData[dayId] = {
            closed: !wh.isActive,
            intervals: wh.intervals.map(interval => ({
              start: interval.startTime.substring(0, 5),
              end: interval.endTime.substring(0, 5),
              isActive: interval.isActive
            }))
          };
        });
      }

      setSalonData(newData);
    }
  }, [workingHours]);

  const handleWorkingHoursChange = (dayId: string, field: string, value: boolean) => {
    setSalonData(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        [field]: value,
      },
    }));
  };

  const handleIntervalChange = (dayId: string, intervalIndex: number, field: 'start' | 'end', value: string) => {
    setSalonData(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        intervals: prev[dayId].intervals.map((interval, index) => (index === intervalIndex ? { ...interval, [field]: value } : interval)),
      },
    }));
  };

  const addInterval = (dayId: string) => {
    setSalonData(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        intervals: [...prev[dayId].intervals, { start: '09:00', end: '17:00', isActive: true }],
      },
    }));
  };

  const removeInterval = (dayId: string, intervalIndex: number) => {
    setSalonData(prev => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        intervals: prev[dayId].intervals.filter((_, index) => index !== intervalIndex),
      },
    }));
  };

  const handleSave = async () => {
    try {
      await onSave(salonData);
      setIsEditingHours(false);
    } catch (error) {
      console.error('Chyba při ukládání working hours:', error);
    }
  };

  return (
    <>
      {/* Working Hours */}
      <section className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            <FontAwesomeIcon icon={faClock} className="mr-2" />
            Pracovní doby
          </h3>
          <div className="flex items-center space-x-2">
            {!isEditingHours ? (
              <button 
                onClick={() => {
                  // Inicializace dat pokud nejsou
                  if (Object.keys(salonData).length === 0) {
                    const newData: WorkingHoursData = {};
                    days.forEach(day => {
                      newData[day.id] = { closed: true, intervals: [{ start: '09:00', end: '17:00', isActive: true }] };
                    });
                    setSalonData(newData);
                  }
                  setIsEditingHours(true);
                }} 
                className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <FontAwesomeIcon icon={faEdit} className="mr-1" />
                Upravit
              </button>
            ) : (
              <>
                <button 
                  onClick={() => setIsEditingHours(false)} 
                  className="flex items-center px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  Zrušit
                </button>
                <button 
                  onClick={handleSave} 
                  className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <FontAwesomeIcon icon={faSave} className="mr-1" />
                  Uložit
                </button>
              </>
            )}
          </div>
        </div>
        <div className="space-y-4">
          {Object.keys(salonData).length === 0 ? (
            <div className="text-center py-8">
              <FontAwesomeIcon icon={faClock} className="text-gray-300 text-4xl mb-2" />
              <p className="text-gray-500">Pracovní doba není nastavena</p>
              <p className="text-sm text-gray-400 mt-1">Klikněte na "Upravit" pro nastavení</p>
            </div>
          ) : (
            days
              .filter(day => {
                const dayData = salonData[day.id];
                // V edit mode zobrazit všechny dny, jinak pouze otevřené nebo s aktivními intervaly
                return dayData && (isEditingHours || !dayData.closed || dayData.intervals.some(interval => interval.isActive));
              })
              .map(day => {
                const dayData = salonData[day.id];
                return (
              <div key={day.id} className="flex items-center space-x-4">
                <div className="w-20 text-sm font-medium text-gray-700">{day.name}</div>
                <div className="flex-1 flex items-center space-x-2">
                  {isEditingHours ? (
                    <>
                      <input
                        type="checkbox"
                        checked={!dayData.closed}
                        onChange={e => handleWorkingHoursChange(day.id, 'closed', !e.target.checked)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <div className="flex-1 flex flex-wrap items-center gap-2">
                        {dayData.intervals.map((interval, index) => (
                          <div key={index} className="flex items-center space-x-2 bg-gray-50 rounded-lg p-2">
                            <input
                              type="time"
                              value={interval.start}
                              onChange={e => handleIntervalChange(day.id, index, 'start', e.target.value)}
                              disabled={dayData.closed}
                              className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                            />
                            <span className="text-gray-500">-</span>
                            <input
                              type="time"
                              value={interval.end}
                              onChange={e => handleIntervalChange(day.id, index, 'end', e.target.value)}
                              disabled={dayData.closed}
                              className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                            />
                            {dayData.intervals.length > 1 && (
                              <button
                                onClick={() => removeInterval(day.id, index)}
                                className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                                title="Odebrat interval"
                              >
                                <FontAwesomeIcon icon={faMinus} className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ))}
                        {!dayData.closed && (
                          <button
                            onClick={() => addInterval(day.id)}
                            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg border border-dashed border-green-300 hover:border-green-400 transition-colors"
                            title="Přidat interval"
                          >
                            <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <span className="text-sm text-gray-900">
                      {dayData.closed ? (
                        <span className="text-red-600 font-medium">Zavřeno</span>
                      ) : (
                        dayData.intervals.map((interval, index) => (
                          <span key={index}>
                            {interval.start} - {interval.end}
                            {index < dayData.intervals.length - 1 && <span className="text-gray-400 mx-2">•</span>}
                          </span>
                        ))
                      )}
                    </span>
                  )}
                </div>
              </div>
              );
            })
          )}
        </div>
      </section>
    </>
  );
};

export default WorkingHours;
